import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { useIsExpandedContext } from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewLineItemsList";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import { picklistsSelectors } from "store/selectors";
import { fetchMultipleOrganizations } from "store/actions/picklists";
import { getDirectCostSubtotal, getLineItemsDirectCostTotal } from "utils/budgetBuilder";

import styles from "./styles.css";


const getUnloadedOrganizations = (childData, organizations) => {
	const orgIds = [];
	for (let childKey in childData) {
		const children = childData[childKey];
		if (isNonEmptyArray(children)) {
			for (let child of children) {
				const orgId = child.value;
				if (!orgIds.includes(child.value) && !organizations.find(org => org.value === orgId)) {
					orgIds.push(orgId);
				}
			}
		}
	}

	return orgIds;
};

const getChildrenCostData = (categoryData, numberOfPeriods) => {
	let result = {};
	for (let i = 0; i < categoryData.length; i++) {
		let category = categoryData[i];
		let children = category?.items || [];
		children.forEach(child => {
			let key = child.value;
			let childData = result[key] || getDefaultSponsoredCostsList(numberOfPeriods, child.value);
			childData[i] = child;
			result[key] = childData;
		});
	}

	return result;
};

const getDefaultSponsoredCostsList = (numberOfPeriods, value) => Array.from(Array(numberOfPeriods).keys())
	.map(() => ({ value }));

export const OverviewSubawardsRow = ({ categoryData, hash, periods }) => {
	const { currencyFormatter } = useTenantFormatter();

	const { options: organizations } = useSelector(picklistsSelectors.organizations);

	const { isExpandedState: { [hash]: isExpanded }, isExpandedDispatch } = useIsExpandedContext();

	const setIsExpanded = useCallback(
		isExpanded => isExpandedDispatch({ type: hash, isExpanded }),
		[ hash, isExpandedDispatch ],
	);

	useEffect(() => {
		isExpandedDispatch({ type: hash, isExpanded: true });
	}, [ hash, isExpandedDispatch ]);

	const handleIsExpandedChange = isExpanded => {
		setIsExpanded(isExpanded);
	};

	const childData = useMemo(
		() => getChildrenCostData(categoryData, periods.length),
		[ categoryData, periods.length ],
	);

	useEffect(() => {
		if (isNonEmptyArray(organizations)) {
			const orgIds = getUnloadedOrganizations(childData, organizations);
			if (isNonEmptyArray(orgIds)) {
				fetchMultipleOrganizations(orgIds);
			}
		}
	}, [ childData, organizations ]);

	return (
		<>
			{isExpanded && <>
				<Table.Row>
					<Table.Cell className={`${styles.cellAlignLeft} ${styles.expandCell}`}>
						<span>{"Subawards"}</span>
						{isExpanded && <div className={styles.expandIcon} onClick={() => handleIsExpandedChange(!isExpanded)}>
							<i className="fa fa-caret-down" aria-hidden="true" />
						</div>}
					</Table.Cell>
					{periods?.map((period, p) => (
						<Table.Cell key={`period-${p}`}>
							<div className={styles.cellAlignRight}>
								<span className={styles.bold}>{"Amount"}</span>
							</div>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight} />
				</Table.Row>
				{Object.entries(childData)?.map(([ i, items ]) => (
					<Table.Row key={`item-${i}`}>
						<Table.Cell className={styles.cellAlignLeft}>
							<span>{organizations.find(org => org.id === items[0]?.value)?.text}</span>
						</Table.Cell>
						{items.map((item, p) => (
							<Table.Cell key={`period-${i}-${p}`}>
								<div className={styles.cellAlignRight}>
									<span>{currencyFormatter(item?.directCost, 0)}</span>
								</div>
							</Table.Cell>
						))}
						<Table.Cell className={styles.cellAlignRight}>
							<span className={styles.bold}>{currencyFormatter(getDirectCostSubtotal({ items }))}</span>
						</Table.Cell>
					</Table.Row>
				))}
			</>}
			<Table.Row>
				<Table.Cell className={`${styles.cellAlignLeft} ${!isExpanded ? styles.expandCell : undefined}`}>
					<span className={isExpanded ? styles.bold : undefined}>{`Subawards ${isExpanded ? "Total" : ""}`}</span>
					{!isExpanded && <div className={styles.expandIcon} onClick={() => handleIsExpandedChange(!isExpanded)}>
						<i className="fa fa-caret-right" aria-hidden="true" />
					</div>}
				</Table.Cell>
				{Object.entries(categoryData).map(([ p, categories ]) => (
					<Table.Cell className={styles.cellAlignRight} key={`period-${p}`}>
						<span className={isExpanded ? styles.bold : undefined}>
							{currencyFormatter(getDirectCostSubtotal(categories))}
						</span>
					</Table.Cell>
				))}
				<Table.Cell className={styles.cellAlignRight}>
					<span className={styles.bold}>{currencyFormatter(getLineItemsDirectCostTotal(categoryData))}</span>
				</Table.Cell>
			</Table.Row>
		</>
	);
};
